// RichTextArea component matching Product Description from original HTML

import React, { useRef, useEffect, useCallback, useState } from 'react'
import { TextAreaProps } from '../../types'
import { useResponsive, useValidation } from '../../hooks'

interface RichTextAreaProps extends Omit<TextAreaProps, 'rows'> {
  showToolbar?: boolean
  toolbarItems?: string[]
  minHeight?: string
}

function RichTextArea({
  value,
  onChange,
  placeholder,
  maxLength = 2000,
  autoResize = true,
  validation = [],
  showCharacterCount = true,
  showToolbar = true,
  toolbarItems = ['bold', 'italic', 'underline', 'list', 'link'],
  disabled = false,
  readOnly = false,
  minHeight = '140px',
  className = '',
  ...props
}: RichTextAreaProps): JSX.Element {
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const textAreaId = `rich-textarea-${Math.random().toString(36).substring(7)}`
  
  const { breakpoint } = useResponsive()
  const validationResult = useValidation(value, validation)
  
  // Auto-resize functionality
  const adjustHeight = useCallback(() => {
    const textArea = textAreaRef.current
    if (!textArea || !autoResize) return
    
    // Reset height to auto to get the correct scrollHeight
    textArea.style.height = 'auto'
    
    // Calculate new height based on content
    const newHeight = Math.max(
      textArea.scrollHeight,
      parseInt(minHeight) || 140
    )
    
    // Set the new height with a max limit for very long content
    textArea.style.height = `${Math.min(newHeight, 400)}px`
  }, [autoResize, minHeight])
  
  // Adjust height when value changes
  useEffect(() => {
    adjustHeight()
  }, [value, adjustHeight])
  
  // Adjust height after component mounts
  useEffect(() => {
    adjustHeight()
  }, [])
  
  // Handle input change with character limit enforcement
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let newValue = e.target.value
    
    // Enforce maxLength if specified
    if (maxLength && newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength)
      
      // Update the textarea value to show truncated text
      if (textAreaRef.current) {
        textAreaRef.current.value = newValue
      }
    }
    
    onChange(newValue)
  }, [onChange, maxLength])
  
  // Handle toolbar actions
  const handleToolbarAction = useCallback((action: string) => {
    if (!textAreaRef.current || disabled || readOnly) return
    
    const textArea = textAreaRef.current
    const start = textArea.selectionStart
    const end = textArea.selectionEnd
    const selectedText = value.substring(start, end)
    
    let newValue = value
    let newCursorPosition = start
    
    switch (action) {
      case 'bold':
        newValue = value.substring(0, start) + `**${selectedText || 'bold text'}**` + value.substring(end)
        newCursorPosition = selectedText ? end + 4 : start + 2
        break
      case 'italic':
        newValue = value.substring(0, start) + `*${selectedText || 'italic text'}*` + value.substring(end)
        newCursorPosition = selectedText ? end + 2 : start + 1
        break
      case 'underline':
        newValue = value.substring(0, start) + `__${selectedText || 'underlined text'}__` + value.substring(end)
        newCursorPosition = selectedText ? end + 4 : start + 2
        break
      case 'list':
        const listItem = `- ${selectedText || 'List item'}\n`
        newValue = value.substring(0, start) + listItem + value.substring(end)
        newCursorPosition = start + listItem.length
        break
      case 'link':
        const link = `[${selectedText || 'link text'}](url)`
        newValue = value.substring(0, start) + link + value.substring(end)
        newCursorPosition = selectedText ? start + selectedText.length + 3 : start + 11
        break
    }
    
    onChange(newValue)
    
    // Restore cursor position
    setTimeout(() => {
      if (textAreaRef.current) {
        textAreaRef.current.focus()
        textAreaRef.current.setSelectionRange(newCursorPosition, newCursorPosition)
      }
    }, 0)
  }, [value, onChange, disabled, readOnly])
  
  // Container classes matching original HTML
  const containerClasses = [
    'tw-w-full tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-overflow-hidden',
    'focus-within:tw-ring-2 focus-within:tw-ring-primary focus-within:tw-border-transparent tw-transition-all',
    validationResult.error && 'tw-border-red-500 focus-within:tw-ring-red-500',
    className
  ].filter(Boolean).join(' ')
  
  // Toolbar classes
  const toolbarClasses = [
    'tw-bg-gray-50 dark:tw-bg-[#233648] tw-px-3 tw-py-2',
    'tw-border-b tw-border-gray-300 dark:tw-border-[#364152] tw-flex tw-gap-2'
  ].join(' ')
  
  // TextArea classes matching original HTML
  const textAreaClasses = [
    'tw-w-full tw-bg-transparent tw-border-none tw-p-4',
    'tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500',
    'focus:tw-ring-0 tw-resize-none',
    autoResize ? 'tw-overflow-hidden' : 'tw-overflow-auto',
    disabled && 'tw-cursor-not-allowed tw-opacity-50'
  ].join(' ')
  
  // Character count styling
  const characterCount = value?.length || 0
  const isNearLimit = maxLength && characterCount > maxLength * 0.8
  const isOverLimit = maxLength && characterCount > maxLength
  
  // Toolbar button component
  const ToolbarButton = ({ action, icon, title }: { action: string; icon: string; title: string }) => (
    <button
      type="button"
      onClick={() => handleToolbarAction(action)}
      className="tw-p-1 tw-rounded hover:tw-bg-gray-200 dark:hover:tw-bg-[#364152] tw-text-gray-500 dark:tw-text-gray-400 tw-transition-colors"
      title={title}
      disabled={disabled || readOnly}
    >
      <span className="material-symbols-outlined tw-text-[18px]">{icon}</span>
    </button>
  )
  
  const toolbarConfig = {
    bold: { icon: 'format_bold', title: 'Bold' },
    italic: { icon: 'format_italic', title: 'Italic' },
    underline: { icon: 'format_underlined', title: 'Underline' },
    list: { icon: 'format_list_bulleted', title: 'Bullet List' },
    link: { icon: 'link', title: 'Insert Link' }
  }
  
  return (
    <div className="tw-w-full">
      <div className={containerClasses}>
        {/* Toolbar */}
        {showToolbar && (
          <div className={toolbarClasses}>
            {toolbarItems.map((item) => (
              <ToolbarButton
                key={item}
                action={item}
                icon={toolbarConfig[item as keyof typeof toolbarConfig]?.icon || 'help'}
                title={toolbarConfig[item as keyof typeof toolbarConfig]?.title || item}
              />
            ))}
          </div>
        )}
        
        {/* TextArea */}
        <textarea
          ref={textAreaRef}
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          className={textAreaClasses}
          style={{ minHeight: minHeight }}
          aria-invalid={!!validationResult.error}
          aria-describedby={
            showCharacterCount ? `${textAreaId}-count` : undefined
          }
          {...props}
        />
      </div>
      
      {/* Character count and limit info */}
      {showCharacterCount && (
        <div className="tw-flex tw-justify-between tw-items-center tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mt-1">
          <span>Max {maxLength} characters.</span>
          <span 
            id={`${textAreaId}-count`}
            className={[
              isOverLimit 
                ? 'tw-text-red-500 tw-font-semibold' 
                : isNearLimit 
                  ? 'tw-text-orange-500' 
                  : 'tw-text-gray-500 dark:tw-text-gray-400'
            ].join(' ')}
          >
            {characterCount}/{maxLength}
          </span>
        </div>
      )}
      
      {/* Character limit warning */}
      {maxLength && isNearLimit && !isOverLimit && (
        <div className="tw-text-xs tw-text-orange-500 tw-mt-1 tw-flex tw-items-center tw-gap-1">
          <svg className="tw-w-3 tw-h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Approaching character limit
        </div>
      )}
      
      {isOverLimit && (
        <div className="tw-text-xs tw-text-red-500 tw-mt-1 tw-flex tw-items-center tw-gap-1">
          <svg className="tw-w-3 tw-h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          Character limit exceeded
        </div>
      )}
    </div>
  )
}

export default RichTextArea